export interface EventTriggerOption {
  value: string;
  label: string;
  comingSoon?: boolean;
}

// Event triggers shown in the message-rule form dropdown. Functional triggers
// omit `comingSoon`. "Coming soon" triggers can still be selected, but any rule
// that uses one is locked disabled (the host can't enable it) until the feature
// ships — see `isComingSoonEventTrigger` + the RulesTab enable guard.
export const EVENT_TRIGGER_OPTIONS: EventTriggerOption[] = [
  { value: "new_booking", label: "New Booking" },
  // Task #4705 — reminder scheduled at (booking-request arrival + property
  // auto-approval window − 2h), auto-cancelled at send time when the member
  // already submitted the form / a decision was made / the stay left the
  // booking-request bucket. UNLIKE new_booking, the booking-form link token
  // is REQUIRED (hard server validation) for an enabled rule.
  { value: "booking_form_followup", label: "Booking Form Not Completed (before auto-approval deadline)" },
  // Fire when the host approves/rejects a booking-request submission on the
  // Booking Requests tab (the same action that stamps the member decision).
  { value: "booking_approved", label: "Booking Approved" },
  { value: "booking_rejected", label: "Booking Rejected" },
  { value: "payment_received", label: "Payment Received", comingSoon: true },
  { value: "payment_overdue", label: "Payment Overdue", comingSoon: true },
  // Fires when a member submits a maintenance request from their PadPulse
  // member portal, or when a PadSplit-synced task arrives with member
  // attribution (exact padsplit-member-id match only).
  { value: "maintenance_request", label: "Maintenance Request" },
  // Fire on PadSplit-sync status transitions: an existing member landing in
  // the "active" bucket (move_in) or the explicit moved-out status flip
  // (move_out — deliberately NOT the missing-from-scrape archival, which is
  // too weak a signal to auto-message on).
  { value: "move_in", label: "Move-in" },
  { value: "move_out", label: "Move-out" },
];

const COMING_SOON_EVENT_TRIGGERS: ReadonlySet<string> = new Set(
  EVENT_TRIGGER_OPTIONS.filter((o) => o.comingSoon).map((o) => o.value),
);

export function isComingSoonEventTrigger(value: string | null | undefined): boolean {
  return !!value && COMING_SOON_EVENT_TRIGGERS.has(value);
}

// True when a rule is an event rule wired to a "Coming soon" trigger. Such a
// rule is "stuck disabled": it must render as off and can't be enabled until the
// feature ships, regardless of any `enabled: true` value persisted earlier.
export function isComingSoonEventRule(
  rule: { ruleType?: string | null; eventTrigger?: string | null },
): boolean {
  return rule.ruleType === "event" && isComingSoonEventTrigger(rule.eventTrigger);
}

// Shown when a host tries to enable a rule whose event trigger is still
// "Coming soon".
export const COMING_SOON_RULE_ENABLE_TOAST =
  "Cannot enable messages for 'Coming soon' event types or event triggers, check back later";

// Shown when a host without the incoming_message_rules beta grant tries to
// enable an Incoming Message rule (e.g. one pre-filled by Pulse).
export const INCOMING_BETA_ENABLE_TOAST =
  "Incoming Message rules are in beta — contact PadPulse support to request access";
